/**
 * Which basemap tiles the map draws, and under what attribution.
 *
 * A pure `(key) => {url, attribution}` decision, lifted out of the map's
 * creation effect for the same reason every other decision in this feature was
 * lifted: so it can be asserted without a Leaflet instance, a canvas or a
 * browser.
 *
 * ## Why terrain
 *
 * This is network *design* software: the question a planner asks of the map is
 * "why is that lane expensive", and the answer is very often terrain — a range
 * between two nodes, a river with few crossings, a corridor that follows a
 * valley because nothing else can. A flat street basemap (CARTO Positron, which
 * this map used to draw) renders all of that as identical white, so the map
 * showed where the facilities were and nothing about why the network looked the
 * way it did.
 *
 * Esri's World Topo Map has shaded relief, vegetation, water and labels, and is
 * tiled to zoom 23 — deep enough for a single facility.
 *
 * ## Why not the obvious alternatives
 *
 * - **Esri World Terrain Base** is tiled only to zoom 13, and is a near-white
 *   layer with no labels or roads meant to be stacked *under* a reference
 *   layer. At facility zoom it has nothing to draw.
 * - **OpenTopoMap** is the best-looking of the three, but its hypsometric tint
 *   is a saturated orange-to-green ramp that competes with the facility markers
 *   and flow lines drawn on top of it — on this map the data has to win. Its
 *   tiles also come from a small volunteer server whose usage policy rules out
 *   an application like this one.
 *
 * Both were checked by fetching a tile and *looking at the pixels*, for the
 * reason in the next section.
 *
 * ## Why there is still a key
 *
 * CARTO began requiring an API key for their raster basemaps in late August
 * 2026. Unauthenticated requests still return HTTP 200 and still return a valid
 * PNG — they just have "API KEY REQUIRED / carto.com/basemaps/apikey" printed
 * diagonally across the image. Nothing in a status code, a content type or a
 * byte count shows it; only the pixels do. The same change hit Home Assistant,
 * Grafana's Geomap panel and openHAB in the same week.
 *
 * `VITE_CARTO_API_KEY` therefore remains as an opt-in for anyone who wants the
 * flat light cartography back and holds a key. It is no longer the default, and
 * no longer the thing an unset variable degrades away from.
 *
 * See `docs/superpowers/plans/2026-09-12-carto-basemap-key.md`.
 */

/** What Leaflet needs to build a tile layer. */
export interface BasemapSource {
  url: string
  attribution: string
  maxZoom: number
  /**
   * Only for providers that serve `{s}` sharded hostnames. Absent means the URL
   * has no `{s}` in it — Esri serves from one host — and the caller must then
   * leave Leaflet's own default alone rather than pass `undefined` through,
   * which would break substitution for the providers that do shard.
   */
  subdomains?: string
}

/**
 * Esri's World Topo Map — shaded relief, vegetation, water, roads and labels.
 *
 * Note the tile path is `{z}/{y}/{x}`: ArcGIS orders it row-then-column, the
 * opposite of the XYZ convention every other provider here uses. Swapping them
 * does not error — it silently serves the tile from somewhere else entirely, so
 * the map looks like it is working while showing the wrong part of the world.
 *
 * The attribution is Esri's own `copyrightText`, read from the service metadata
 * at `.../World_Topo_Map/MapServer?f=json` rather than copied from a tutorial,
 * and abridged to the sources that cover the areas this app serves. OpenStreetMap
 * is in it because Esri's own copyright line names them.
 */
export const TERRAIN_SOURCE: BasemapSource = {
  url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
  attribution:
    'Tiles &copy; <a href="https://www.esri.com/">Esri</a> — Esri, HERE, Garmin, Intermap, USGS, FAO, NPS, NRCAN, GeoBase, IGN, Ordnance Survey, METI, &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  maxZoom: 19,
}

/** CARTO's Positron ("light_all") — the cartography this map used to draw. */
export const CARTO_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'

/**
 * Picks the basemap for a given key.
 *
 * `key` is `import.meta.env.VITE_CARTO_API_KEY` in the app. Blank, whitespace
 * and `undefined` all mean "no key" — a `.env` line left as `VITE_CARTO_API_KEY=`
 * produces the empty string, not `undefined`, and a key of `""` appended to the
 * URL would be worse than no key at all: it is a request CARTO rejects rather
 * than one it merely watermarks.
 */
export function basemapSource(key: string | undefined): BasemapSource {
  const trimmed = key?.trim()
  if (!trimmed) return TERRAIN_SOURCE

  return {
    url: `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png?api_key=${encodeURIComponent(trimmed)}`,
    attribution: CARTO_ATTRIBUTION,
    subdomains: 'abcd',
    maxZoom: 18,
  }
}
